import { Box, Fade, Paper, Popper } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'

interface SnackbarTooltipProps {
  containerRef: React.RefObject<HTMLDivElement | null>
  message: string
  open: boolean
  onClose?: () => void
  placement?: 'top' | 'bottom' | 'left' | 'right'
  autoHideDuration?: number
  offset?: [number, number]
  maxWidth?: number
}

const SnackbarTooltip = ({
  containerRef,
  message,
  open,
  onClose,
  placement = 'bottom',
  autoHideDuration = 3000,
  offset = [0, 8],
  maxWidth = 300,
}: SnackbarTooltipProps) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    if (open && containerRef?.current) {
      setAnchorEl(containerRef?.current)

      // Auto hide after duration
      timeoutRef.current = setTimeout(() => {
        onClose?.()
      }, autoHideDuration)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [open, containerRef, onClose, autoHideDuration])

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      style={{ zIndex: 9999 }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: offset,
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <Paper
            elevation={6}
            sx={{
              p: '6px 16px',
              bgcolor: 'rgba(33, 33, 33, 0.9)',
              color: theme.palette.common.white,
              borderRadius: 1,
              maxWidth: maxWidth,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: '14px',
                  letterSpacing: '0.17px',
                  lineHeight: '20.02px',
                  color: theme.palette.common.white,
                  flex: 1,
                }}
              >
                {message}
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}

export default SnackbarTooltip
